import {
  useLayoutEffect,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react"
import { Box, Stack } from "@mui/material"
import { alpha } from "@mui/material/styles"

/** PostBot-only sizing; never changes the surrounding application shell. */
export function PostBotPageLayout({ children }: PropsWithChildren) {
  return (
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        maxWidth: 1180,
        mx: "auto",
        px: { xs: 2, sm: 3 },
        minWidth: 0,
        boxSizing: "border-box",
        "& .MuiDivider-root": {
          borderColor: (theme) => alpha(theme.palette.text.primary, 0.14),
        },
      }}
    >
      {children}
    </Stack>
  )
}

export function PostBotEditorLayout({
  children,
  preview,
}: PropsWithChildren<{ preview: ReactNode }>) {
  const container = useRef<HTMLDivElement>(null)
  const aside = useRef<HTMLElement>(null)
  const [sizing, setSizing] = useState({ wide: false, sticky: false })
  useLayoutEffect(() => {
    const measure = () => {
      const wide = (container.current?.clientWidth ?? 0) >= 840
      const sticky =
        wide &&
        (aside.current?.offsetHeight ?? Infinity) <= window.innerHeight - 48
      setSizing((previous) =>
        previous.wide === wide && previous.sticky === sticky
          ? previous
          : { wide, sticky },
      )
    }
    measure()
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(measure)
    if (container.current) observer?.observe(container.current)
    if (aside.current) observer?.observe(aside.current)
    window.addEventListener("resize", measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [])
  return (
    <Box
      ref={container}
      sx={{
        display: "grid",
        gridTemplateColumns: sizing.wide
          ? "minmax(0, 1fr) 350px"
          : "minmax(0, 1fr)",
        gap: 3,
        alignItems: "start",
        minWidth: 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>{children}</Box>
      <Box
        ref={aside}
        component="aside"
        aria-label="Предпросмотр сообщения"
        sx={{
          width: "100%",
          maxWidth: 350,
          minWidth: 0,
          mx: "auto",
          position: sizing.sticky ? "sticky" : "static",
          top: 24,
        }}
      >
        {preview}
      </Box>
    </Box>
  )
}
